import React from 'react'
// @src imports
import { AccountRoute } from 'src/dashboard/routes/AccountRoute'
import { AwardsRoute } from 'src/dashboard/routes/AwardsRoute'
import { CardHistoryRoute } from 'src/dashboard/routes/CardHistoryRoute'
import { DraftedCardsRoute } from 'src/dashboard/routes/DraftedCardsRoute'
import { DraftedOrdersRoute } from 'src/dashboard/routes/DraftedOrdersRoute'
import { NotificationsRoute } from 'src/dashboard/routes/NotificationsRoute'
import { PaymentSettingsRoute } from 'src/dashboard/routes/PaymentSettingsRoute'

import AccountDropdown from './AccountDropdown/AccountDropdown'
import { Text } from '@sendoutcards/quantum-design-ui'
import { useSelector } from 'src/hooks'

const styles = {
  dashboardHeader: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: 'auto',
    padding: '0 2rem',
    display: 'flex',
    flexDlow: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    '@media (max-width: 815px)': {
      padding: 0,
    },
    '@media (max-width: 500px)': {
      justifyContent: 'center',
      '> div:last-child': {
        display: 'none',
      },
    },
  },
  headerSubtitle: {
    display: 'flex',
    alignItems: 'baseline',
    '@media (max-width: 1550px)': {
      flexDirection: 'column' as const,
    },
  },
  flexRowWrap: {
    display: 'flex',
    flexFlow: 'row wrap',
    '@media (max-width: 500px)': {
      marginTop: '1rem',
    },
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
    '@media (max-width: 670px)': {
      alignItems: 'flex-start',
      paddingLeft: 20,
    },
    '@media (max-width: 500px)': {
      alignItems: 'center',
      paddingLeft: 0,
    },
  },
}

type DashboardHeaderProps = {
  routePath: string
}

const DashboardHeader: React.FC<DashboardHeaderProps> = props => {
  const { routePath } = props
  const { isMobile } = useSelector(s => s.window)

  return (
    <div css={styles.dashboardHeader}>
      <div css={styles.flexRowWrap}>
        <div css={styles.flexColumn}>
          {routePath === AccountRoute.path && (
            <>
              <Text type="subtitle">My Account</Text>
              <div css={styles.headerSubtitle}>
                <Text type="caption" css={{ letterSpacing: '1' }}>
                  Manage your account information. &nbsp;
                </Text>
                {!isMobile && (
                  <Text type="footnote">
                    Any changes made here apply to SendOutCards, Prompting U,
                    Blue 42 for Life, Stream VA, Stream Marketing, and Bella
                    Cadeaux.
                  </Text>
                )}
              </div>
            </>
          )}
          {routePath === AwardsRoute.path && (
            <>
              <Text type="subtitle">My Awards</Text>
              <Text type="caption">View awards progress</Text>
            </>
          )}
          {routePath === PaymentSettingsRoute.path && (
            <>
              <Text type="subtitle">My Payment Settings</Text>
              <Text type={{ xSmall: 'footnote', medium: 'caption' }}>
                Change your credit card and fund your expense bucket
              </Text>
            </>
          )}
          {routePath === DraftedOrdersRoute.path && (
            <>
              <Text type="subtitle">My Drafted Orders</Text>
              <Text type="caption">Manage unsent orders</Text>
            </>
          )}
          {routePath === DraftedCardsRoute.path && (
            <>
              <Text type="subtitle">My Drafted Cards</Text>
              <Text type="caption">Manage saved and unsent cards</Text>
            </>
          )}
          {routePath === NotificationsRoute.path && (
            <>
              <Text type="subtitle">My Notifications</Text>
              <Text type="caption">View system notifications</Text>
            </>
          )}
          {routePath === CardHistoryRoute.path && (
            <>
              <Text type="subtitle">My Card History</Text>
              <Text type="caption">View status on sent cards</Text>
            </>
          )}
        </div>
      </div>
      <AccountDropdown />
    </div>
  )
}

export default DashboardHeader
